import { PayloadAction } from '@reduxjs/toolkit'
import { actions as appActions } from '../state'
import { toast } from 'react-toastify'
import { ReturnCall } from '../types'
import { actions, initialState } from './SwapOrReturnState'
import {
  GetSwapsOrReturnsPayload,
  CancelSwapOrReturnPayload,
  GetSwapsOrReturnsStatusEnum,
  GetDataForRequestCreationPayload,
  Request,
  SwapOrReturnTypeIds,
  CreateOrUpdateRequestPayload,
  ValidateSwapOrReturnToResolvePayload,
  StartOrDelaySwapOrReturnToResolvePayload,
  FinishSwapOrReturnToResolvePayload,
  SwapOrReturnToResolveItemNotFoundPayload,
  CountBatchesToReturnSagaPayload,
  SwapAddOrRemoveReceiveBatchesPayload,
  ValidateRequestsToResolvePayload,
  ValidateSupplierReleasePayload,
  ReleaseReturnRequestPayload,
} from './SwapOrReturnTypes'
import {
  all,
  call,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects'
import apis from './SwapOrReturnApi'
import { selectFilteredStatus } from './SwapOrReturnSelectors'

export function* getSwapsOrReturns(
  data: PayloadAction<GetSwapsOrReturnsPayload>,
) {
  try {
    yield put(appActions.isLoading(true))
    yield put(actions.setProperty({ property: 'swapOrReturnInvoices', value: initialState.swapOrReturnInvoices }))

    const response: ReturnCall<typeof apis.getSwapsOrReturns> = yield call(
      apis.getSwapsOrReturns,
      data.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Não foi possível obter as notas!')
      return
    }

    yield put(actions.setProperty({ property: 'swapOrReturnSearchData', value: data.payload }))
    yield put(actions.setProperty({ property: 'filteredStatus', value: data.payload.status || [] }))
    yield put(actions.setProperty({ property: 'pagination', value: response.data.pagination }))
    yield put(actions.setProperty({ property: 'swapOrReturnInvoices', value: response.data.data }))
  } catch (error) {
    toast.error('Não foi possível obter as notas!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* cancelSwapOrReturn(
  data: PayloadAction<CancelSwapOrReturnPayload>,
) {
  try {
    yield put(appActions.isLoading(true))

    const { id, onSuccess } = data.payload

    const response: ReturnCall<typeof apis.cancelSwapOrReturn> = yield call(
      apis.cancelSwapOrReturn,
      id,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Não foi possível cancelar nota!')
      return
    }

    const filteredStatus: string[] = yield select(selectFilteredStatus)

    if (filteredStatus.length === 0 || filteredStatus.includes(GetSwapsOrReturnsStatusEnum.CANCELED)) {
      yield put(actions.setSwapOrReturnToCancelStatus(id))
    } else {
      yield put(actions.removeSwapOrReturnInvoice(id))
    }

    onSuccess()
  } catch (error) {
    toast.error('Não foi possível cancelar nota!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getDataForRequestCreation(
  data: PayloadAction<GetDataForRequestCreationPayload>,
) {
  yield put(appActions.isLoading(true))
  yield put(actions.clearState())

  try {
    const { setModalData } = data.payload
    const result: ReturnCall<typeof apis.getDataForRequestCreation> = yield call(
      apis.getDataForRequestCreation,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível obter informações de troca ou devolução!')
      return
    }

    setModalData({ show: true, swapOrReturnData: result.data })
  } catch (error) {
    toast.error('Não foi possível obter informações de troca ou devolução!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getRequestProductsToEdit(
  data: PayloadAction<number>,
) {
  yield put(appActions.isLoading(true))
  yield put(actions.clearState())
  const idRequest = data.payload

  try {
    const result: ReturnCall<typeof apis.getRequestProductsToEdit> = yield call(
      apis.getRequestProductsToEdit,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || `Não foi possível buscar os produtos da pendência ${idRequest} para edição!`)
      return
    }

    const requestToEdit: Request | null = result.data.swap || result.data.return

    const selectedProducts = requestToEdit?.products.map((product) => {
      if (!product.inputQuantity) return product

      const totalInputQuantity = product.batches.reduce((acc, item) => acc + (item.inputQuantity || 0), 0)

      return {
        ...product,
        isQuantityMatch: product.inputQuantity === totalInputQuantity && product.inputQuantity > 0,
      }
    })

    const dataWithAvailableForAddition = { ...result.data, availableProductsForCreation: requestToEdit?.availableProductsForAddition || [] }
    yield put(actions.setProperty({ property: 'swapOrReturnData', value: dataWithAvailableForAddition }))
    yield put(actions.setProperty({ property: 'selectedProducts', value: selectedProducts }))
    yield put(actions.setProperty({ property: 'requestTypeId', value: result.data.swap ? SwapOrReturnTypeIds.SWAP : SwapOrReturnTypeIds.RETURN }))
    yield put(actions.setProperty({ property: 'typeAction', value: 'EDIT' }))
  } catch (error) {
    toast.error(`Não foi possível buscar os produtos da pendência ${idRequest} para edição!`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* createOrUpdateRequest(
  data: PayloadAction<CreateOrUpdateRequestPayload>,
) {
  yield put(appActions.isLoading(true))
  const { setShowTagPrinterModal } = data.payload

  try {
    const result: ReturnCall<typeof apis.createOrUpdateRequest> = yield call(
      apis.createOrUpdateRequest,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível salvar a solicitação!')
      return
    }

    yield put(actions.setProperty({ property: 'requestTagInfo', value: result.data }))
    setShowTagPrinterModal(true)
  } catch (error) {
    toast.error('Não foi possível salvar a solicitação!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getSwapOrReturnToResolve(
  data: PayloadAction<number>,
) {
  yield put(appActions.isLoading(true))
  yield put(actions.setProperty({ property: 'swapOrReturnToResolveData', value: null }))

  try {
    const result: ReturnCall<typeof apis.getSwapOrReturnToResolve> = yield call(
      apis.getSwapOrReturnToResolve,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || `Não foi possível buscar a pendência '${data.payload}' para resolução!`)
      return
    }

    result.data.items.forEach((item) => {
      item.returnBatches.forEach((returnBatch) => {
        returnBatch.expirationBatches.sort((a, b) => a.idLocationType - b.idLocationType)
      })
    })

    yield put(actions.setProperty({ property: 'swapOrReturnToResolveData', value: result.data }))
  } catch (error) {
    toast.error(`Não foi possível buscar a pendência '${data.payload}' para resolução!`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* startSwapOrReturnToResolve(
  data: PayloadAction<StartOrDelaySwapOrReturnToResolvePayload>,
) {
  yield put(appActions.isLoading(true))

  const { id, idFulfillmentCenter, onSuccess } = data.payload

  try {
    if (!idFulfillmentCenter) {
      toast.error('Fulfillment center não informado! Tente recarregar a página.')
      return
    }

    const result: ReturnCall<typeof apis.startSwapOrReturnToResolve> = yield call(
      apis.startSwapOrReturnToResolve,
      id,
      idFulfillmentCenter,
    )

    if (!result.isOk) {
      if (result?.data?.data?.newStatus) {
        yield put(actions.swapOrReturnToResolveDataUpdateStatus(result?.data?.data?.newStatus))
      }
      toast.error(result?.data?.data?.message || `Não foi possível iniciar a resolução da pendência pendência '${data.payload}'!`)
      return
    }

    yield put(actions.swapOrReturnToResolveDataUpdateStatus(result.data.newStatus))

    onSuccess()
  } catch (error) {
    toast.error(`Não foi possível iniciar a resolução da pendência pendência '${data.payload}'!`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* delaySwapOrReturnToResolve(
  data: PayloadAction<StartOrDelaySwapOrReturnToResolvePayload>,
) {
  yield put(appActions.isLoading(true))

  const { id, onSuccess } = data.payload

  try {
    const result: ReturnCall<typeof apis.delaySwapOrReturnToResolve> = yield call(
      apis.delaySwapOrReturnToResolve,
      id,
    )

    if (!result.isOk) {
      if (result?.data?.data?.newStatus) {
        yield put(actions.swapOrReturnToResolveDataUpdateStatus(result?.data?.data?.newStatus))
      }
      toast.error(result?.data?.data?.message || `Não foi possível adiar a resolução da pendência pendência '${data.payload}'!`)
      return
    }

    yield put(actions.resetAllCountedQuantities())

    yield put(actions.swapOrReturnToResolveDataUpdateStatus(result.data.newStatus))

    onSuccess()
  } catch (error) {
    toast.error(`Não foi possível adiar a resolução da pendência pendência '${data.payload}'!`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* validateSwapOrReturnToResolve(
  data: PayloadAction<ValidateSwapOrReturnToResolvePayload>,
) {
  yield put(appActions.isLoading(true))
  const { onSuccess, onError } = data.payload
  try {
    const result: ReturnCall<typeof apis.validateSwapOrReturnToResolve> = yield call(
      apis.validateSwapOrReturnToResolve,
      data.payload,
    )

    if (!result.isOk) {
      onError(result?.data?.data?.message || 'Não foi possível validar o produto!')
      return
    }

    onSuccess()
  } catch (error) {
    onError('Não foi possível validar o produto!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* countBatchesToReturn(
  data: PayloadAction<CountBatchesToReturnSagaPayload>,
) {
  yield put(appActions.isLoading(true))
  const { onSuccess, onError } = data.payload

  try {
    const result: ReturnCall<typeof apis.countBatchesToReturn> = yield call(
      apis.countBatchesToReturn,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível contar os lotes à retornar!')
      onError()
      return
    }

    yield put(actions.updateItemBatchesToReturn(data.payload))

    onSuccess()
  } catch (error) {
    toast.error('Não foi possível contar os lotes à retornar!')
    onError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* swapAddOrRemoveReceiveBatches(
  data: PayloadAction<SwapAddOrRemoveReceiveBatchesPayload>,
) {
  yield put(appActions.isLoading(true))
  const { onSuccess, onError } = data.payload

  try {
    const result: ReturnCall<typeof apis.swapAddOrRemoveReceiveBatches> = yield call(
      apis.swapAddOrRemoveReceiveBatches,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível adicionar ou remover lotes à receber!')
      onError()
      return
    }

    yield put(actions.updateItemBatchesToReceive({
      idItem: result.data.idItem,
      batches: result.data.batches,
    }))

    onSuccess(result.data)
  } catch (error) {
    toast.error('Não foi possível adicionar ou remover lotes à receber!')
    onError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* undoItemCount(
  data: PayloadAction<number>,
) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.undoItemCount> = yield call(
      apis.undoItemCount,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível desfazer a contagem!')
      return
    }

    yield put(actions.resetCountedQuantity(data.payload))
  } catch (error) {
    toast.error('Não foi possível desfazer a contagem!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* finishSwapOrReturnToResolve(
  data: PayloadAction<FinishSwapOrReturnToResolvePayload>,
) {
  yield put(appActions.isLoading(true))

  const { idRequest, onSuccess, onError } = data.payload

  try {
    const result: ReturnCall<typeof apis.finishSwapOrReturnToResolve> = yield call(
      apis.finishSwapOrReturnToResolve,
      data.payload,
    )

    if (!result.isOk) {
      if (result?.data?.data?.newStatus) {
        yield put(actions.swapOrReturnToResolveDataUpdateStatus(result?.data?.data?.newStatus))
      }
      toast.error(result?.data?.data?.message || `Não foi possível finalizar a resolução da pendência '${idRequest}'!`)
      onError()
      return
    }

    yield put(actions.swapOrReturnToResolveDataUpdateStatus(result.data.newStatus))

    onSuccess(result.data.allFinished, result.data.pendingsToRelease)
  } catch (error) {
    toast.error(`Não foi possível finalizar a resolução da pendência '${idRequest}'!`)
    onError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* swapOrReturnToResolveItemNotFound(
  data: PayloadAction<SwapOrReturnToResolveItemNotFoundPayload>,
) {
  yield put(appActions.isLoading(true))

  const { itemId, onSuccess } = data.payload

  try {
    const result: ReturnCall<typeof apis.swapOrReturnToResolveItemNotFound> = yield call(
      apis.swapOrReturnToResolveItemNotFound,
      itemId,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível cancelar a pendência desse produto!')
      return
    }

    if (result?.data?.isPendingCanceled) {
      yield put(actions.swapOrReturnToResolveDataUpdateStatus(result.data.status))
    }

    yield put(actions.setProductNotFound(itemId))

    onSuccess()
  } catch (error) {
    toast.error('Não foi possível cancelar a pendência desse produto!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* validateRequestsToResolve(
  data: PayloadAction<ValidateRequestsToResolvePayload>,
) {
  yield put(appActions.isLoading(true))

  const { onSuccess, onError } = data.payload

  try {
    const result: ReturnCall<typeof apis.validateRequestsToResolve> = yield call(
      apis.validateRequestsToResolve,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível validar as pendências para resolução!')
      onError()
      return
    }

    onSuccess()
  } catch (error) {
    toast.error('Não foi possível validar as pendências para resolução!')
    onError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* validateSupplierRelease(
  data: PayloadAction<ValidateSupplierReleasePayload>,
) {
  yield put(appActions.isLoading(true))

  const { onSuccess, onError, hideErrorMessage } = data.payload

  try {
    const result: ReturnCall<typeof apis.validateSupplierRelease> = yield call(
      apis.validateSupplierRelease,
      data.payload,
    )

    if (!result.isOk) {
      if (!hideErrorMessage) {
        toast.error(result?.data?.data?.message || 'Não foi possível validar a liberação do fornecedor!')
      }
      onError()
      return
    }

    onSuccess(result.data.idsRequestsWaitingRelease)
  } catch (error) {
    toast.error('Não foi possível validar a liberação do fornecedor!')
    onError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* releaseReturnRequest(
  data: PayloadAction<ReleaseReturnRequestPayload>,
) {
  yield put(appActions.isLoading(true))

  const { onSuccess } = data.payload

  try {
    const result: ReturnCall<typeof apis.releaseReturnRequest> = yield call(
      apis.releaseReturnRequest,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível confirmar conclusão da pendência!')
      return
    }

    onSuccess()
  } catch (error) {
    toast.error('Não foi possível confirmar conclusão da pendência!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export default function* () {
  yield all([
    takeLatest(actions.getSwapsOrReturns.type, getSwapsOrReturns),
    takeLatest(actions.cancelSwapOrReturn.type, cancelSwapOrReturn),
    takeLatest(actions.getDataForRequestCreation.type, getDataForRequestCreation),
    takeLatest(actions.getRequestProductsToEdit.type, getRequestProductsToEdit),
    takeLatest(actions.createOrUpdateRequest.type, createOrUpdateRequest),
    takeLatest(actions.getSwapOrReturnToResolve.type, getSwapOrReturnToResolve),
    takeLatest(actions.startSwapOrReturnToResolve.type, startSwapOrReturnToResolve),
    takeLatest(actions.delaySwapOrReturnToResolve.type, delaySwapOrReturnToResolve),
    takeLatest(actions.validateSwapOrReturnToResolve.type, validateSwapOrReturnToResolve),
    takeLatest(actions.countBatchesToReturn.type, countBatchesToReturn),
    takeLatest(actions.swapAddOrRemoveReceiveBatches.type, swapAddOrRemoveReceiveBatches),
    takeLatest(actions.undoItemCount.type, undoItemCount),
    takeLatest(actions.finishSwapOrReturnToResolve.type, finishSwapOrReturnToResolve),
    takeLatest(actions.swapOrReturnToResolveItemNotFound.type, swapOrReturnToResolveItemNotFound),
    takeLatest(actions.validateRequestsToResolve.type, validateRequestsToResolve),
    takeLatest(actions.validateSupplierRelease.type, validateSupplierRelease),
    takeLatest(actions.releaseReturnRequest.type, releaseReturnRequest),
  ])
}
